<template>
  <div class="c-details-dialog" :key="updateKey">
    <v-dialog
      v-model="showDialog.show"
      scrollable
      content-class="dialog-content"
      max-width="95%"
    >
      <v-card>
        <v-card-title>
          Reporting {{ showDialog.repByProject ? 'in the project': currentTaskData.Is_Folder ? 'in a folder' : 'in the task' }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12"
            >
              <v-data-table
                class="data-table"
                :headers="headers"
                fixed-header
                height="60vh"
                :items="items"
                :items-per-page="10"
                :footer-props="{
                  'items-per-page-options': [10, 20, 30, 40, 50, 100]
                }"
                @update:page="goTo"
              >
                <!-- Header reported -->
                <template v-slot:[`header.Real_Dur`]="{ header }">
                  {{ header.text }}<br>
                  (total: {{ totalReported }})
                </template>
                <!-- /Header reported -->
                <!-- Header paid -->
                <template v-slot:[`header.Report_Dur`]="{ header }">
                  {{ header.text }}<br>
                  (total: {{ totalPaid }})
                </template>
                <!-- /Header paid -->
                <!-- Header invoice -->
                <template v-slot:[`header.Invoice_Dur`]="{ header }">
                  {{ header.text }}<br>
                  (total: {{ totalInvoiced }})
                </template>
                <!-- /Header invoice -->
                <!-- Quality Indicator -->
                <template v-slot:[`header.Quality`]="{ header }">
                  {{ header.text }}
                </template>
                <!-- /Quality Indicator -->
                <!-- Actions -->
                <template v-slot:[`item.Delete`]="{ item }">
                  <v-btn small icon inline color="primary" class="d-inline-block" :disabled="item.ReadOnly">
                    <v-icon
                      @click="addNewReport(item)"
                    >
                      mdi-pencil-circle
                    </v-icon>
                  </v-btn>
                  <v-btn small icon color="red darken-2" class="d-inline-block">
                    <v-icon
                      @click="openRemoveModal(item)"
                    >
                      mdi-delete-circle
                    </v-icon>
                  </v-btn>
                </template>
                <!-- /Actions -->
                <!-- Start date -->
                <template v-slot:[`item.Start_Date`]="{ item }">
                  <DatePicker
                    v-if="!item.Jira_Report_ID"
                    v-model="item.Start_Date"
                    @onDatePickerChange="changeParam(item)"
                    dense
                  />
                  <span v-else>{{ item.Start_Date }}</span>
                </template>
                <!-- /Start date -->
                <!-- Est./Rep. -->
                <template v-slot:[`item.EstRep`]="{ item }">
                  {{ (estRepForTask[item.Task_ID] ? estRepForTask[item.Task_ID].est : 0) | valToFixed }}/{{ (estRepForTask[item.Task_ID] ? estRepForTask[item.Task_ID].rep : 0) | valToFixed }}
                </template>
                <!-- /Est./Rep. -->
                <!-- Reported -->
                <template v-slot:[`item.Real_Dur`]="{ item }">
                  <v-menu
                    v-if="!item.ReadOnly"
                    v-model="reportedDurationMenu[item.uId]"
                    :nudge-right="40"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item.Real_Dur"
                        prepend-icon="mdi-clock-time-four-outline mdi-18px"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="reportedDurationMenu[item.uId]"
                      v-model="item.Real_Dur"
                      format="24hr"
                      :allowed-minutes="allowedMinutes"
                      full-width
                      @click:minute="changeParam(item)"
                    ></v-time-picker>
                  </v-menu>
                  <span v-else>{{ item.Real_Dur }}</span>
                </template>
                <!-- /Reported -->
                <!-- Paid -->
                <template v-slot:[`item.Report_Dur`]="{ item }">
                  <v-menu
                    v-if="!item.ReadOnly"
                    v-model="paidDurationMenu[item.uId]"
                    :nudge-right="40"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item.Report_Dur"
                        prepend-icon="mdi-clock-time-four-outline mdi-18px"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="paidDurationMenu[item.uId] && !item.Jira_Report_ID"
                      v-model="item.Report_Dur"
                      format="24hr"
                      :allowed-minutes="allowedMinutes"
                      full-width
                      @click:minute="changeParam(item)"
                    ></v-time-picker>
                  </v-menu>
                  <span v-else>{{ item.Report_Dur }}</span>
                </template>
                <!-- /Paid -->
                <!-- Invoiced -->
                <template v-slot:[`item.Invoice_Dur`]="{ item }">
                  <v-menu
                    v-if="!item.ReadOnly"
                    v-model="invoiceDurationMenu[item.uId]"
                    :nudge-right="40"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item.Invoice_Dur"
                        prepend-icon="mdi-clock-time-four-outline mdi-18px"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="invoiceDurationMenu[item.uId]"
                      v-model="item.Invoice_Dur"
                      format="24hr"
                      :allowed-minutes="allowedMinutes"
                      full-width
                      @click:minute="changeParam(item)"
                    ></v-time-picker>
                  </v-menu>
                  <span v-else>{{ item.Invoice_Dur }}</span>
                </template>
                <!-- /Invoiced -->
                <!-- Activity -->
                <template v-slot:[`item.Activity_Type`]="{ item }">
                  <v-select
                    v-if="!item.ReadOnly"
                    v-model="item.Activity_Type"
                    :items="activityTypes"
                    item-text="Description"
                    item-value="Activity_Type"
                    @change="changeParam(item)"
                  >
                  </v-select>
                  <span v-else>{{ activityTypeText(item.Activity_Type) }}</span>
                </template>
                <!-- /Activity -->
                <!-- Description -->
                <template v-slot:[`item.Description`]="props">
                  <span class="editable-field" @click.prevent="!props.item.ReadOnly && toggleDescriptionDialog(props.item)">{{ props.item.Description }}</span>
                </template>
                <!-- /Description -->
                <!-- Quality Indicator -->
                <template v-slot:[`item.Quality`]="{ item }">
                  <BadWords
                    :description="item.Description"
                    :hours="item.Real_Dur"
                    :bad-words="badWords"
                  />
                </template>
                <!-- /Quality Indicator -->
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="currentTaskData && !currentTaskData.Is_Folder"
            small
            color="success"
            @click="addNewReport(false)"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Create new report -->
    <v-dialog
      v-model="createNewReportDialog"
      v-if="createNewReportDialog"
      scrollable
      content-class="dialog-content"
    >
      <v-card>
        <v-card-title>
          <div>
            {{ editReport ? 'Edit report': 'New report' }}
            <div
              class="subtitle-2"
              :class="[totalsFor.est <= totalsFor.rep ? 'red--text darken-3': 'success--text darken-3']"
            >
              Estimate: {{ totalsFor.est | valToFixed }} / Reported: {{ totalsFor.rep | valToFixed }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="createNewReportDialog = false; editReport = false;"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <CreateNewReport v-model="newReportData" :tasks="newReportFor" @currentTaskId="totalsFor = e"/>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click.prevent="createNewReport">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Create new report -->
    <!-- Edit description -->
    <v-dialog
      v-model="showTextAreaDialog"
      content-class="dialog-content"
      scrollable
      min-width="300"
      max-width="70vw"
    >
      <v-card>
        <div class="pa-2 description-col">
          <div class="description-wrapper">
            <div class="description-with-indicator">
              <DescriptionWithSpellCheck
                v-if="currentItem"
                :key="'textarea-' + showTextAreaDialog"
                label="Description"
                v-model="currentItem.Description"
              />
              <BadWords
                v-if="currentItem"
                class="bad-words-overlay"
                :description="currentItem.Description"
                :hours="currentItem.Real_Dur"
                :bad-words="badWords"
              />
            </div>
          </div>
        </div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small outlined color="primary" class="ml-auto" @click.prevent="cancelDescription">
            Cancel
          </v-btn>
          <v-btn small color="primary" @click.prevent="save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Edit description -->
    <!-- Remove dialog -->
    <ConfirmOrDeniedDialog
      v-if="removeModal"
      v-model="removeModal"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'You can\'t restore this report after delete'"
      @onDeleteClick="deleteReport"
      @onCancelClick="reportIdToRemove = null; removeModal = false"
      @onCloseClick="reportIdToRemove = null; removeModal = false"
    />
    <!-- /Remove dialog -->
  </div>
</template>
<script>
import { sharedReportingMixin } from '@/mixins/sharedReportingMixin'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'DetailsDialog',
  mixins: [sharedReportingMixin],
  components: {
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue'),
    CreateNewReport: () => import('@/components/tasks_editor/CreateNewReport.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    BadWords: () => import('@/components/global/BadWords.vue')
  },
  data () {
    return {
      updateKey: 1,
      removeModal: false,
      reportIdToRemove: null,
      totalReported: 0,
      totalPaid: 0,
      totalInvoiced: 0,
      headers: [
        {
          text: 'Employee',
          align: 'start',
          value: 'Name',
          class: 'text-no-wrap',
          cellClass: 'text-no-wrap',
          divider: true
        },
        { text: 'Project', value: 'ProjectName', class: 'text-no-wrap', divider: true },
        { text: 'Task name', value: 'TaskName', class: 'text-no-wrap', divider: true },
        { text: 'Start date', value: 'Start_Date', width: 150, divider: true },
        { text: 'Est./Rep.', value: 'EstRep', class: 'text-no-wrap', divider: true },
        { text: 'Reported', value: 'Real_Dur', width: 150, class: 'text-no-wrap', divider: true },
        { text: 'Paid', value: 'Report_Dur', width: 150, class: 'text-no-wrap', divider: true },
        { text: 'Invoiced', value: 'Invoice_Dur', width: 150, class: 'text-no-wrap', divider: true },
        { text: 'Activity', value: 'Activity_Type', width: 150, divider: true },
        { text: 'Description', value: 'Description', width: 250, cellClass: 'description-cell', divider: true },
        {
          text: '#',
          align: 'center',
          value: 'Delete',
          sortable: false,
          width: 80,
          cellClass: 'remove-cell',
          divider: true
        }
      ],
      items: [],
      newReportFor: [],
      currentItem: null,
      newReportData: {
        Project_ID: null,
        Task_ID: null,
        Employee_ID: null,
        Start_Date: null,
        Start_Time: null,
        Real_Dur: null,
        Report_Dur: null,
        Invoice_Dur: null,
        Description: null,
        Activity_Type: null
      },
      createNewReportDialog: false,
      showTextAreaDialog: false,
      originalDescription: '',
      editReport: false,
      reportedDurationMenu: {},
      paidDurationMenu: {},
      invoiceDurationMenu: {},
      badWords: []
    }
  },
  mounted () {
    this.getDetails()
    this.fetchBadWords()
  },
  computed: {
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    activityTypeText () {
      return val => {
        if (!val) return ''
        const match = this.activityTypes.find(item => item.Activity_Type === val)
        return match ? match.Description : val
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    showDialog: {
      get () {
        return this.$store.getters.showDetailsDialog
      },
      set (val) {
        this.$store.dispatch('showDetailsDialog', { show: val })
      }
    },
    estRepForTask () {
      return this.$store.getters.getEstRepAtTasks
    },
    totalsFor () {
      return this.newReportData.Task_ID ? this.estRepForTask[this.newReportData.Task_ID] : { est: 0, rep: 0 }
    },
    selectedProject () {
      return this.$store.getters.getSelectedProject
    },
    currentTaskData () {
      return this.$store.getters.getCurrentTaskData
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    settings () {
      return this.$store.getters.getSettings
    },
    employeeRoles () {
      return this.$store.getters.getEmployeeRoles
    },
    selectedProjectName () {
      return this.$store.getters.getSelectedProjectName
    }
  },
  filters: {
    valToFixed (val) {
      if (typeof val !== 'number') return 0
      if (Number.isInteger(val)) return val
      const intPart = Math.floor(val)
      const firstDecimalDigit = Math.floor((val - intPart) * 10)

      if ([0, 1, 2, 3, 4].includes(firstDecimalDigit)) {
        return intPart
      } else if ([6, 7, 8, 9].includes(firstDecimalDigit)) {
        return intPart + 1
      } else if (firstDecimalDigit === 5) {
        return intPart + 0.5
      }
      return val
    }
  },
  methods: {
    async fetchBadWords () {
      this.badWords = await this.getBadWords()
    },
    canReport (projectName) {
      const excludedProjects = ['DC Vacations', 'DC Sick-Leave']
      const isFinancier = this.employeeRoles.financier.val

      if (!isFinancier && excludedProjects.includes(projectName)) {
        this.$store.dispatch('setError', { errorMsg: 'Only Financier role can Save/Update this project report' })
        return false
      }
      return true
    },
    addTime (time1, time2) {
      const hours1 = +time1.split(':')[0]
      const hours2 = +time2.split(':')[0]
      const minutes1 = +time1.split(':')[1]
      const minutes2 = +time2.split(':')[1]
      const resOfMinutes = (minutes1 + minutes2) / 60 | 0
      const sumOfHours = resOfMinutes + hours1 + hours2
      const sumOfMinutes = (minutes1 + minutes2) - (60 * resOfMinutes)
      return ('' + sumOfHours).padStart(2, '0') + ':' + ('' + sumOfMinutes).padStart(2, '0')
    },
    totalTime (items, key) {
      let total = items[0][key]
      for (let i = 1; i < items.length; i++) {
        const time2 = items[i][key]
        total = this.addTime(total, time2)
      }
      return total
    },
    goTo () {
      this.$vuetify.goTo(
        '.data-table tbody tr',
        {
          offset: 0,
          container: '.v-data-table__wrapper'
        }
      )
    },
    changeParam (item) {
      this.currentItem = item
      this.save()
    },
    addNewReport (row) {
      if (row) {
        this.newReportData = { ...row }
        this.editReport = true
      } else {
        this.newReportFor = []
        const allreadyPushed = []
        this.items.forEach(item => {
          if (allreadyPushed.indexOf(item.Task_ID) === -1) {
            allreadyPushed.push(item.Task_ID)
            this.newReportFor.push({
              projectId: this.selectedProject,
              taskId: item.Task_ID,
              taskName: item.TaskName,
              employeeId: item.Employee_ID,
              employeeName: item.Name
            })
          }
        })
        this.newReportData.Project_ID = this.newReportFor[0] ? this.newReportFor[0].projectId : this.selectedProject
        this.newReportData.Task_ID = this.newReportFor[0] ? this.newReportFor[0].taskId : this.currentTaskData.Task_ID
        this.newReportData.Employee_ID = this.newReportFor[0] ? this.newReportFor[0].employeeId : this.currentTaskData.Assigned_To
        this.newReportData.Start_Date = formatLocalDate()
        this.newReportData.Start_Time = '00:00'
        this.newReportData.Real_Dur = null
        this.newReportData.Report_Dur = null
        this.newReportData.Invoice_Dur = '00:00'
        this.newReportData.Description = ''
        this.newReportData.Activity_Type = this.settings.defaultActivityType
        this.newReportData.Project_Name = this.selectedProjectName
      }
      this.createNewReportDialog = true
    },
    createNewReport () {
      let isValidData = true
      for (const item in this.newReportData) {
        if (['Jira_Report_ID'].indexOf(item) > -1 || ['Redmine_Report_ID'].indexOf(item) > -1 || ['ReadOnly'].indexOf(item) > -1) continue
        if (!this.newReportData[item]) {
          this.$store.dispatch('setError', { errorMsg: 'Please fill all fields' })
          isValidData = false
          break
        }
      }
      if (isValidData) {
        if (this.editReport) {
          this.currentItem = this.newReportData
          this.save()
        } else {
          if (this.canReport(this.newReportData.Project_Name)) {
            this.axios.post('/api/reporting/', this.newReportData, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(response => {
              this.$store.dispatch('setSuccess', { successMsg: 'Report successfully added' })
              this.$store.dispatch('updateTasks', { update: true })
              this.getDetails()
              this.createNewReportDialog = false
              this.newReportData = {
                Project_ID: null,
                Task_ID: null,
                Employee_ID: null,
                Start_Date: null,
                Start_Time: null,
                Real_Dur: null,
                Report_Dur: null,
                Invoice_Dur: null,
                Description: null,
                Activity_Type: null
              }
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          }
        }
      }
    },
    openRemoveModal (item) {
      this.reportIdToRemove = item.Reporting_ID
      this.removeModal = true
    },
    deleteReport () {
      this.axios.delete('/api/reporting/' + this.reportIdToRemove, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setSuccess', { successMsg: 'Report successfully deleted' })
        this.$store.dispatch('updateTasks', { update: true })
        this.reportIdToRemove = null
        this.removeModal = false
        this.getDetails()
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
        this.removeModal = false
      })
    },
    setEmployeeForTask (e) {
      this.newReportData.Employee_id = this.newReportFor.filter(item => item.taskId === e)[0].employeeId
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    },
    save () {
      if (this.canReport(this.currentItem.ProjectName)) {
        this.axios.put('/api/reporting/' + this.currentItem.Reporting_ID, this.currentItem, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
          this.$store.dispatch('setSuccess', { successMsg: 'Reporting successfully updated' })
          this.$store.dispatch('updateTasks', { update: true })
          this.getDetails()
          this.showTextAreaDialog = false
          this.createNewReportDialog = false
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    cancelDescription () {
      this.showTextAreaDialog = !this.showTextAreaDialog
      this.currentItem.Description = this.originalDescription
      this.originalDescription = ''
    },
    toggleDescriptionDialog (item) {
      if (item) this.currentItem = item
      this.showTextAreaDialog = !this.showTextAreaDialog
      this.originalDescription = item.Description
    },
    closeDialog () {
      this.$store.dispatch('showDetailsDialog', {
        show: {
          show: false,
          repByProject: false
        }
      })
    },
    getDetails () {
      const url = this.showDialog.repByProject ? `/api/repbyproject/${this.selectedProject}` : `/api/repbytask/${this.currentTaskData.Task_ID}`
      this.axios.get(url, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data.length) {
          this.totalReported = this.totalTime(res.data, 'Real_Dur')
          this.totalPaid = this.totalTime(res.data, 'Report_Dur')
          this.totalInvoiced = this.totalTime(res.data, 'Invoice_Dur')
        } else {
          this.totalReported = 0
          this.totalPaid = 0
          this.totalInvoiced = 0
        }
        this.items = res.data
        this.reportedDurationMenu = {}
        this.paidDurationMenu = {}
        this.invoiceDurationMenu = {}
        for (let i = 0; i < this.items.length; i++) {
          this.items[i].Activity_Type = this.items[i].Activity_Type
            ? this.items[i].Activity_Type.toUpperCase()
            : this.items[i].Activity_Type
          this.$set(this.items[i], 'uId', 'itemId_' + i)
          this.$set(this.reportedDurationMenu, 'itemId_' + i, false)
          this.$set(this.paidDurationMenu, 'itemId_' + i, false)
          this.$set(this.invoiceDurationMenu, 'itemId_' + i, false)
          this.items[i].Start_Date = formatLocalDate(this.items[i].Start_Date)
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>

<style lang="scss">
  .description-cell {
    word-break: break-all;
  }
  .remove-cell {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .editable-field {
    cursor: pointer;
  }

  .c-details-dialog {
    .description-col {
      .description-wrapper {
        position: relative;
      }
    }
  }

.description-with-indicator {
  position: relative;
}

.bad-words-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}
</style>
